import { Menu } from 'antd';
import React, { useEffect, useState } from 'react';
import { StyledSidebar } from './styles';
import { SidebarItem, SidebarMenuGroup, SidebarProps } from './types';
import { Grid } from 'antd';

const { useBreakpoint } = Grid;

function isMenuGroup(item: SidebarItem): item is SidebarMenuGroup {
    return (item as SidebarMenuGroup).type === 'group';
}

function buildMenuItems(items: SidebarItem[]): any[] {
    return items.map((item) => {
        if (isMenuGroup(item)) {
            return {
                key: item.key,
                label: item.label,
                children: buildMenuItems(item.children),
            };
        } else {
            return {
                key: item.key,
                icon: item.icon,
                label: item.label,
                children: item.children
                    ? buildMenuItems(item.children)
                    : undefined,
            };
        }
    });
}

export const Sidebar: React.FC<SidebarProps> = ({
    logo,
    items = [],
    onSelect,
    selectedKey,
    selectedKeys,
    collapsed: controlledCollapsed,
    collapsedWidth = 80,
    collapsible = true,
    breakpoint = 'lg',
    width = 256,
    trigger,
    onBreakpoint,
    onCollapse,
    className,
    style,
    showTrigger = true,
    reverseArrow = false,
    openKeys,
}) => {
    const screens = useBreakpoint();

    const [internalCollapsed, setInternalCollapsed] = useState(false);
    const [internalOpenKeys, setInternalOpenKeys] = useState<string[]>();
    const isControlled = controlledCollapsed !== undefined;
    const collapsed = isControlled ? controlledCollapsed : internalCollapsed;

    const isBelowMd = !screens.md;

    useEffect(() => {
        setInternalOpenKeys(openKeys);
    }, [openKeys]);

    const handleCollapse = (
        newCollapsed: boolean,
        type: 'clickTrigger' | 'responsive'
    ) => {
        if (!isControlled) {
            setInternalCollapsed(newCollapsed);
        }
        onCollapse?.(newCollapsed, type);
    };

    const handleSelect = ({
        key,
        keyPath,
    }: {
        key: string;
        keyPath: string[];
    }) => {
        onSelect?.(key, keyPath);
    };

    const menuItems = buildMenuItems(items);

    return (
        <StyledSidebar
            className={className}
            style={style}
            collapsed={collapsed}
            collapsedWidth={collapsedWidth}
            collapsible={collapsible}
            breakpoint={breakpoint}
            width={width}
            trigger={showTrigger ? trigger : null}
            onBreakpoint={onBreakpoint}
            onCollapse={handleCollapse}
            reverseArrow={reverseArrow}
        >
            {logo && (
                <div className="sidebar-logo w-full flex items-center gap-2">
                    {logo}
                    {!collapsed && (
                        <span className="text-3xl font-semibold text-primary">
                            CMP
                        </span>
                    )}
                </div>
            )}

            <Menu
                className={`text-R-16 ${isBelowMd ? 'mt-2' : 'mt-8 pt-4 overflow-y-scroll [scrollbar-width:none]'}`}
                mode="inline"
                items={menuItems}
                selectedKeys={
                    selectedKeys && selectedKeys.length
                        ? selectedKeys
                        : selectedKey
                          ? [selectedKey]
                          : []
                }
                openKeys={internalOpenKeys}
                onOpenChange={setInternalOpenKeys}
                onSelect={handleSelect}
                style={{ borderRight: 'none', background: 'transparent' }}
            />
        </StyledSidebar>
    );
};
